import React, { useState } from "react";
import axios from "axios";

const emptySearchForm = {
  vehicleNumber: "",
  dlNumber: "",
  dob: "",
  phoneNumber: "",
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return dateStr; 
}

function getBadgeClass(dateStr) {
  if (!dateStr) return { label: "N/A", cls: "bg-slate-700/50 text-slate-400" };
  let d;
  const m1 = dateStr.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
  const m2 = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m1) d = new Date(`${m1[2]} ${m1[1]}, ${m1[3]}`);
  else if (m2) d = new Date(dateStr);
  else return { label: "N/A", cls: "bg-slate-700/50 text-slate-400" };
  if (isNaN(d)) return { label: "N/A", cls: "bg-slate-700/50 text-slate-400" };
  const diff = Math.ceil((d - new Date()) / 86400000);
  if (diff < 0) return { label: "Expired", cls: "bg-red-500/15 text-red-400 ring-1 ring-red-500/30" };
  if (diff <= 30) return { label: "Expiring soon", cls: "bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30" };
  return { label: "Valid", cls: "bg-green-500/15 text-green-400 ring-1 ring-green-500/30" };
}

function Badge({ dateStr }) {
  const { label, cls } = getBadgeClass(dateStr);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide ${cls}`}>
      {label}
    </span>
  );
}

function InfoCell({ label, value, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
      <p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">{label}</p>
      {children || (
        <p className="text-sm font-medium text-gray-900 break-words">{value || "—"}</p>
      )}
    </div>
  );
}

function SectionHeading({ icon, title }) {
  return (
    <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-blue-100">
      <span className="text-xl">{icon}</span>
      <span className="text-sm font-bold text-blue-700 uppercase tracking-wider">{title}</span>
    </div>
  );
}

export default function VehicleRegistration() {
  const [searchForm, setSearchForm] = useState(emptySearchForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleConfirm = async () => {
    if (!searchForm.phoneNumber.trim()) {
      setSaveError("Phone number is required before registering.");
    return;
  }

  if (!/^\d{10}$/.test(searchForm.phoneNumber.trim())) {
    setSaveError("Phone number must be exactly 10 digits.");
    return;
  }

  setSaving(true);
  setSaveError("");
  setSaveSuccess(false);
  try {
    const baseURL = process.env.REACT_APP_BACKEND_URL;
    await axios.post(`${baseURL}/api/ulip/save-registration`, {
      searchForm,
      vehicleDetails: result.vehicleDetails,
      driverDetails: result.driverDetails || null,
    });
    setSaveSuccess(true);
  } catch (err) {
    setSaveError("Failed to save registration. Please try again.");
  } finally {
    setSaving(false);
  }
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchForm({
      ...searchForm,
      [name]: name === "vehicleNumber" 
        ? value.toUpperCase() 
        : name === "phoneNumber" 
        ? value.replace(/\D/g, "")
        :value
    });
  };

  const fetchDetails = async () => {
    if (!searchForm.vehicleNumber.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const baseURL = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(`${baseURL}/api/ulip/fetch-details`, {
        params: {
          vehicleNumber: searchForm.vehicleNumber.trim(),
          dlNumber: searchForm.dlNumber.trim() || undefined,
          dob: searchForm.dob || undefined,
        },
      });
      setResult(res.data);
    } catch (err) {
      setError("Something went wrong while fetching details.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSearchForm(emptySearchForm);
    setResult(null);
    setError("");
    setSaveError("");     
    setSaveSuccess(false);
  };

  const v = result?.vehicleDetails || {};
  const dl = result?.driverDetails || null;
  const isActive = (v.rc_status || "").toUpperCase() === "ACTIVE";

  const dlTrExpiry = dl?.dlTrValdtoDt;
  const dlNtExpiry = dl?.dlNtValdtoDt;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 px-4 py-10 text-gray-900">
      <div className="max-w-4xl mx-auto">

        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-blue-900">Vehicle Registration</h1>
        </div>

        {/* Search card */}
        <div className="bg-white border-2 border-blue-100 rounded-2xl p-8 shadow-lg mb-6 hover:shadow-xl transition-shadow">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

            {/* Vehicle number — full width */}
            <div className="md:col-span-3">
              <label className="block text-sm font-bold text-blue-900 uppercase tracking-wider mb-2">
                Vehicle number <span className="text-red-500">*</span>
              </label>
              <input
                name="vehicleNumber"
                value={searchForm.vehicleNumber}
                onChange={handleChange}
                onKeyDown={(e) => e.key === "Enter" && fetchDetails()}
                placeholder="e.g. DL01AB1234"
                className="w-full h-11 px-4 rounded-lg bg-gray-50 border-2 border-gray-300 text-gray-900 text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white transition"
              />
            </div>

            {/* Phone number */}
            <div>
              <label className="block text-sm font-bold text-blue-900 uppercase tracking-wider mb-2">
                Phone number <span className="text-red-500">*</span>
              </label>
              <input
                name="phoneNumber"
                value={searchForm.phoneNumber}
                onChange={handleChange}
                placeholder="e.g. 9876543210"
                maxLength={10}
                inputMode="numeric"
                className="w-full h-11 px-4 rounded-lg bg-gray-50 border-2 border-gray-300 text-gray-900 text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white transition"
              />
            </div>

            {/* DL number */}
            <div>
              <label className="block text-sm font-bold text-blue-900 uppercase tracking-wider mb-2">
                DL number <span className="text-gray-500 font-normal normal-case text-xs tracking-normal">(optional)</span>
              </label>
              <input
                name="dlNumber"
                value={searchForm.dlNumber}
                onChange={handleChange}
                placeholder="e.g. DL-1234567890"
                className="w-full h-11 px-4 rounded-lg bg-gray-50 border-2 border-gray-300 text-gray-900 text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white transition"
              />
            </div>

            {/* DOB */}
            <div>
              <label className="block text-sm font-bold text-blue-900 uppercase tracking-wider mb-2">
                Date of birth <span className="text-gray-500 font-normal normal-case text-xs tracking-normal">(optional)</span>
              </label>
              <input
                type="date"
                name="dob"
                value={searchForm.dob}
                onChange={handleChange}
                className="w-full h-11 px-4 rounded-lg bg-gray-50 border-2 border-gray-300 text-gray-900 text-sm focus:outline-none focus:border-blue-500 focus:bg-white transition"
              />
            </div>

            {/* Buttons */}
            <div className="flex items-end gap-3">
              <button
                onClick={fetchDetails}
                disabled={loading || !searchForm.vehicleNumber.trim()}
                className="flex-1 h-11 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold shadow-md hover:shadow-lg transition"
              >
                {loading ? "Fetching…" : "Fetch details"}
              </button>
              {result && (
                <button
                  onClick={reset}
                  className="h-11 px-6 rounded-lg border-2 border-gray-300 text-gray-700 hover:text-blue-600 hover:border-blue-300 text-sm font-semibold transition"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mt-6 flex items-start gap-3 bg-red-50 border-2 border-red-200 rounded-xl px-5 py-4">
              <span className="text-red-500 text-xl mt-0.5">⚠</span>
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
          )}
        </div>

        {/* Results */}
        {result && (
          <div className="bg-white border-2 border-blue-100 rounded-2xl p-8 shadow-lg">

            {/* Vehicle strip */}
            <div className="flex items-center gap-5 mb-8 pb-6 border-b-2 border-blue-100">
              <div className="w-16 h-16 rounded-xl bg-blue-100 border-2 border-blue-200 flex items-center justify-center text-4xl shrink-0">
                🚛
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-2xl font-bold text-gray-900 truncate">
                  {[v.rc_maker_desc, v.rc_maker_model].filter(Boolean).join(" ") || "—"}
                </p>
                <p className="text-sm text-gray-600 mt-1 truncate">
                  {[v.rc_body_type_desc, v.rc_vch_catg, v.rc_fuel_desc, v.rc_color].filter(Boolean).join(" · ")}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                  isActive
                    ? "bg-green-50 text-green-700 ring-2 ring-green-200"
                    : "bg-red-50 text-red-700 ring-2 ring-red-200"
                }`}>
                  {isActive ? "✓ Active" : "✕ Inactive"}
                </span>
                <span className="text-xs text-gray-600 font-semibold">
                  {v.rc_regn_no || searchForm.vehicleNumber}
                </span>
              </div>
            </div>

            {/* Vehicle identity */}
            <div className="mb-8">
              <SectionHeading icon="🚗" title="Vehicle identity" />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                <InfoCell label="Registration no." value={v.rc_regn_no} />
                <InfoCell label="Chassis no." value={v.rc_chasi_no} />
                <InfoCell label="Engine no." value={v.rc_eng_no} />
                <InfoCell label="Make" value={v.rc_maker_desc} />
                <InfoCell label="Model" value={v.rc_maker_model} />
                <InfoCell label="Body type" value={v.rc_body_type_desc} />
                <InfoCell label="Fuel type" value={v.rc_fuel_desc} />
                <InfoCell label="Colour" value={v.rc_color} />
                <InfoCell label="Year of manufacture" value={v.rc_manu_month_yr} />
                <InfoCell label="GVW" value={v.rc_gvw} />
                <InfoCell label="Seating capacity" value={v.rc_seat_cap} />
                <InfoCell label="Category" value={v.rc_vch_catg} />
                <InfoCell label="Insurance company" value={v.rc_insurance_comp} />
              </div>
            </div>

            {/* Owner */}
            <div className="mb-8">
              <SectionHeading icon="👤" title="Owner details" />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                <InfoCell label="Owner name" value={v.rc_owner_name} />
                <InfoCell label="Phone number" value={searchForm.phoneNumber} />
                <InfoCell label="RTO code" value={v.rto_cd} />
                <InfoCell label="Registration date" value={v.rc_regn_dt} />
                <div className="col-span-2 md:col-span-4">
                  <InfoCell label="Address" value={v.rc_present_address} />
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="mb-8">
              <SectionHeading icon="🛡️" title="Documents & validity" />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">

                <InfoCell label="Insurance">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-900">{v.rc_insurance_upto || "—"}</span>
                    <Badge dateStr={v.rc_insurance_upto} />
                  </div>
                  <p className="text-xs text-gray-600 truncate">{v.rc_insurance_comp || ""}</p>
                </InfoCell>

                <InfoCell label="Fitness">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">{v.rc_fit_upto || "—"}</span>
                    <Badge dateStr={v.rc_fit_upto} />
                  </div>
                </InfoCell>

                <InfoCell label="PUC">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">{v.rc_pucc_upto || "—"}</span>
                    <Badge dateStr={v.rc_pucc_upto} />
                  </div>
                </InfoCell>

                <InfoCell label="Tax">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">{v.rc_tax_upto || "—"}</span>
                    <Badge dateStr={v.rc_tax_upto} />
                  </div>
                </InfoCell>

                <InfoCell label="National permit">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">{v.rc_np_upto || "—"}</span>
                    <Badge dateStr={v.rc_np_upto} />
                  </div>
                </InfoCell>

                <InfoCell label="5-year permit">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">{v.rc_permit_valid_upto || "—"}</span>
                    <Badge dateStr={v.rc_permit_valid_upto} />
                  </div>
                </InfoCell>

              </div>
            </div>

            {/* Driver licence — only shown if user entered DL number */}
            {searchForm.dlNumber && searchForm.dob && (
              <div>
                <SectionHeading icon="🪪" title="Driver licence" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">

                  {/* Always from user input */}
                  <InfoCell label="Licence no." value={searchForm.dlNumber} />
                  <InfoCell label="Date of birth" value={formatDate(searchForm.dob)} />

                  {/* From SARATHI — only if API returned data */}
                  {dl && (
                    <>
                      <InfoCell label="Transport validity">
                        <p className="text-xs text-gray-600 mb-2">For commercial vehicles</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-900">{formatDate(dlTrExpiry)}</span>
                          <Badge dateStr={dlTrExpiry} />
                        </div>
                      </InfoCell>

                      <InfoCell label="Non-transport validity">
                        <p className="text-xs text-gray-600 mb-2">For personal vehicles</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-900">{formatDate(dlNtExpiry)}</span>
                          <Badge dateStr={dlNtExpiry} />
                        </div>
                      </InfoCell>
                    </>
                  )}

                  {/* SARATHI fetch failed */}
                  {!dl && (
                    <div className="col-span-2 md:col-span-4">
                      <div className="flex items-center gap-3 bg-amber-50 border-2 border-amber-200 rounded-xl px-5 py-4">
                        <span className="text-amber-600 text-lg">⚠</span>
                        <p className="text-sm text-amber-700 font-medium">
                          DL details could not be fetched from SARATHI. Only the entered DL number and DOB are shown.
                        </p>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            )}

            {/* Action bar */}
            <div className="flex flex-col gap-3 mt-8 pt-6 border-t-2 border-blue-100">

              {saveSuccess && (
                <div className="flex items-center gap-3 bg-green-50 border-2 border-green-200 rounded-xl px-5 py-3">
                  <span className="text-green-600">✓</span>
                  <p className="text-sm text-green-700 font-medium">
                    {saveSuccess === "updated"
                      ? "Vehicle details updated successfully."
                      : "Vehicle registered successfully."}
                  </p>
                </div>
              )}

              {saveError && (
                <div className="flex items-center gap-3 bg-red-50 border-2 border-red-200 rounded-xl px-5 py-3">
                  <span className="text-red-500">⚠</span>
                  <p className="text-sm text-red-700 font-medium">{saveError}</p>
                </div>
              )}

              <div className="flex gap-4">
                <button
                  onClick={handleConfirm}
                  disabled={saving || !!saveSuccess}
                  className="flex-1 h-12 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold shadow-md hover:shadow-lg transition"
                >
                  {saving ? "Saving…" : saveSuccess ? "✓ Saved" : "✓ Confirm & register"}
                </button>
                <button
                  onClick={reset}
                  className="h-12 px-6 rounded-lg border-2 border-gray-300 text-gray-700 hover:text-blue-600 hover:border-blue-400 text-sm font-bold transition"
                >
                  Re-fetch
                </button>
              </div>

            </div>

          </div>
        )}
      </div>
    </div>
  );
}